import { useMutation, useQuery, useQueryClient, UseMutationOptions, UseQueryOptions, UseQueryResult } from 'react-query';
import { useFileUploader } from 'src/hooks/useFileUploader';
import { CustomerOrderAttachment } from 'src/types/CustomerOrderAttachment';
import { destroy, get, patch, post } from '../utils';
import useLog from 'src/hooks/useLog';

type CreateCustomerOrderAttachmentInput = Pick<CustomerOrderAttachment, 'fileName' | 'fileType' | 'fileSize'>;
interface UploadCustomerOrderAttachmentInput {
  orderId: string;
  file: File;
}

interface DeleteCustomerOrderAttachmentInput {
  orderId: string;
  attachment: CustomerOrderAttachment;
}

enum CustomerOrderAttachmentQueryKey {
  CustomerOrderAttachments = 'CustomerOrderAttachments',
}

const basePath = (orderId: string) => `customerorder/${orderId}/attachments`;

export function getCustomerOrderAttachments(orderId: string): Promise<CustomerOrderAttachment[]> {
  return get<CustomerOrderAttachment[]>(basePath(orderId));
}

export function createCustomerOrderAttachment(
  orderId: string,
  input: CreateCustomerOrderAttachmentInput
): Promise<CustomerOrderAttachment> {
  return post<CustomerOrderAttachment>(basePath(orderId), input);
}

export async function getCustomerOrderAttachmentUploadUrl(orderId: string, attachmentId: string): Promise<string> {
  const response = await get<{ uploadUrl: string }>(`${basePath(orderId)}/${attachmentId}/upload-url`);
  return response.uploadUrl;
}

export function validateCustomerOrderAttachmentUpload(orderId: string, attachmentId: string): Promise<CustomerOrderAttachment> {
  return patch<CustomerOrderAttachment>(`${basePath(orderId)}/${attachmentId}/validate-upload`);
}

export async function getCustomerOrderAttachmentDownloadUrl(orderId: string, attachmentId: string): Promise<string> {
  const response = await get<{ downloadUrl: string }>(`${basePath(orderId)}/${attachmentId}/download-url`);
  return response.downloadUrl;
}

export function deleteCustomerOrderAttachment(orderId: string, attachmentId: string): Promise<void> {
  return destroy(`${basePath(orderId)}/${attachmentId}`);
}

export function useQueryCustomerOrderAttachments(
  orderId: string,
  options?: UseQueryOptions<CustomerOrderAttachment[], Error>
): UseQueryResult<CustomerOrderAttachment[]> {
  return useQuery([CustomerOrderAttachmentQueryKey.CustomerOrderAttachments, orderId], () => getCustomerOrderAttachments(orderId), options);
}

export function useDeleteCustomerOrderAttachmentMutation(options?: UseMutationOptions<void, Error, DeleteCustomerOrderAttachmentInput>) {
  const queryClient = useQueryClient();

  const log = useLog();
  return useMutation<void, Error, DeleteCustomerOrderAttachmentInput>(
    ({ orderId, attachment }) => deleteCustomerOrderAttachment(orderId, attachment.id),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.refetchQueries([CustomerOrderAttachmentQueryKey.CustomerOrderAttachments, variables.orderId]);
        log.success(`Deleted ${variables.attachment.fileName}`);
        options?.onSuccess?.(data, variables, context);
      },
      onError: (error, variables, context) => {
        log.error(`Failed to delete ${variables.attachment.fileName}: ${error.message}`, error);
        options?.onError?.(error, variables, context);
      },
    }
  );
}

export function useUploadCustomerOrderAttachmentMutation(
  options?: UseMutationOptions<CustomerOrderAttachment, Error, UploadCustomerOrderAttachmentInput>
) {
  const queryClient = useQueryClient();
  const { uploadFile, fileUploadProgress, isUploadingFile } = useFileUploader();

  const log = useLog();
  const mutation = useMutation<CustomerOrderAttachment, Error, UploadCustomerOrderAttachmentInput>(
    async ({ orderId, file }) => {
      const attachment = await createCustomerOrderAttachment(orderId, {
        fileName: file.name,
        fileType: file.type || 'application/octet-stream',
        fileSize: file.size,
      });

      const uploadUrl = await getCustomerOrderAttachmentUploadUrl(orderId, attachment.id);
      const uploadSuccess = await uploadFile(uploadUrl, file);
      if (!uploadSuccess) {
        throw new Error(`Failed to upload ${file.name}`);
      }

      return validateCustomerOrderAttachmentUpload(orderId, attachment.id);
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.refetchQueries([CustomerOrderAttachmentQueryKey.CustomerOrderAttachments, variables.orderId]);
        log.success(`Uploaded ${variables.file.name}`);
        options?.onSuccess?.(data, variables, context);
      },
      onError: (error, variables, context) => {
        log.error(`Failed to upload ${variables.file.name}: ${error.message}`, error);
        options?.onError?.(error, variables, context);
      },
    }
  );

  return {
    ...mutation,
    fileUploadProgress,
    isUploadingFile,
    isProcessingFile: mutation.isLoading || isUploadingFile,
  };
}
